import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Plus, Trash2, Loader2, Save, Upload, ArrowUp, ArrowDown, ImageIcon, Info } from 'lucide-react';
import { getAbout, saveAbout, uploadFile, resolveFileUrl } from '../../services/api';
import { toast } from '../../hooks/use-toast';

const emptySection = () => ({
  id: crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2),
  heading: 'New section',
  body: '',
  image_url: '',
  image_position: 'none',
  order: 0,
});

const AboutAdminPage = () => {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingIdx, setUploadingIdx] = useState(null);
  const heroFileRef = useRef();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAbout();
      setForm({
        hero_title: data.hero_title || '',
        hero_subtitle: data.hero_subtitle || '',
        hero_image: data.hero_image || '',
        sections: (data.sections || []).map((s, i) => ({ ...s, order: s.order || (i + 1) * 10 })),
      });
    } catch (err) {
      toast({ title: 'Failed to load', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const patch = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target?.value ?? e }));
  const patchSection = (idx, field, value) => setForm((f) => ({
    ...f,
    sections: f.sections.map((s, i) => i === idx ? { ...s, [field]: value } : s),
  }));

  const addSection = () => setForm((f) => ({
    ...f,
    sections: [...f.sections, { ...emptySection(), order: (f.sections.length + 1) * 10 }],
  }));
  const removeSection = (idx) => setForm((f) => ({
    ...f,
    sections: f.sections.filter((_, i) => i !== idx),
  }));
  const moveSection = (idx, dir) => {
    setForm((f) => {
      const arr = [...f.sections];
      const j = idx + dir;
      if (j < 0 || j >= arr.length) return f;
      [arr[idx], arr[j]] = [arr[j], arr[idx]];
      return { ...f, sections: arr.map((s, i) => ({ ...s, order: (i + 1) * 10 })) };
    });
  };

  const handleHeroUpload = async (file) => {
    if (!file) return;
    try {
      const res = await uploadFile(file);
      setForm((f) => ({ ...f, hero_image: res.url }));
      toast({ title: 'Hero image uploaded' });
    } catch {
      toast({ title: 'Upload failed', variant: 'destructive' });
    }
  };

  const handleSectionUpload = async (idx, file) => {
    if (!file) return;
    setUploadingIdx(idx);
    try {
      const res = await uploadFile(file);
      patchSection(idx, 'image_url', res.url);
      toast({ title: 'Image uploaded' });
    } catch {
      toast({ title: 'Upload failed', variant: 'destructive' });
    } finally {
      setUploadingIdx(null);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await saveAbout({
        ...form,
        sections: form.sections.map((s, i) => ({ ...s, order: (i + 1) * 10 })),
      });
      toast({ title: 'About page saved' });
    } catch (err) {
      toast({ title: 'Save failed', variant: 'destructive', description: err?.response?.data?.detail || '' });
    } finally {
      setSaving(false);
    }
  };

  if (loading || !form) {
    return (
      <div className="bg-white rounded-2xl border border-black/5 p-10 text-center text-[#0b1a2e]/60">
        <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
      </div>
    );
  }

  return (
    <div data-testid="admin-about-page">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
        <div>
          <span className="text-xs font-semibold tracking-[0.25em] text-[#0f5c3d] uppercase">CMS</span>
          <h1 className="mt-2 text-4xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>
            About Us
          </h1>
          <p className="mt-2 text-sm text-[#0b1a2e]/60">
            Edit the public /about page — hero + reorderable sections with markdown support.
          </p>
        </div>
        <button
          onClick={save}
          disabled={saving}
          data-testid="about-save-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#0f5c3d] text-white text-sm font-medium hover:bg-[#0b1a2e] transition disabled:opacity-60"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save changes
        </button>
      </div>

      {/* Hero */}
      <div className="bg-white border border-black/5 rounded-2xl p-6 mb-6">
        <h2 className="text-lg font-semibold text-[#0b1a2e] mb-4">Hero</h2>
        <div className="space-y-4">
          <label className="block">
            <span className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">Title</span>
            <input
              value={form.hero_title}
              onChange={patch('hero_title')}
              data-testid="about-hero-title"
              className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0f5c3d]/30"
            />
          </label>
          <label className="block">
            <span className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">Subtitle</span>
            <textarea
              rows={2}
              value={form.hero_subtitle}
              onChange={patch('hero_subtitle')}
              className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0f5c3d]/30 resize-y"
            />
          </label>
          <div>
            <span className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">Hero image</span>
            <div className="flex items-start gap-4">
              <div className="w-52 aspect-[16/10] rounded-lg overflow-hidden bg-[#e2ede6] flex items-center justify-center shrink-0">
                {form.hero_image ? (
                  <img src={resolveFileUrl(form.hero_image)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <ImageIcon className="w-6 h-6 text-[#0b1a2e]/30" />
                )}
              </div>
              <div className="flex-1 space-y-2">
                <input ref={heroFileRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleHeroUpload(e.target.files?.[0])} />
                <button
                  type="button"
                  onClick={() => heroFileRef.current?.click()}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-[#0f5c3d]/20 text-sm hover:bg-[#f7fbf8]"
                >
                  <Upload className="w-4 h-4" /> Upload
                </button>
                <input
                  value={form.hero_image}
                  onChange={patch('hero_image')}
                  placeholder="or paste URL"
                  className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-xs outline-none"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sections */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold text-[#0b1a2e]">Sections</h2>
        <button
          onClick={addSection}
          data-testid="about-add-section-btn"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#0f5c3d]/25 text-xs font-medium text-[#0f5c3d] hover:bg-[#f7fbf8]"
        >
          <Plus className="w-3.5 h-3.5" /> Add section
        </button>
      </div>

      <div className="flex items-start gap-2 mb-4 p-3 bg-[#f7fbf8] border border-[#0f5c3d]/15 rounded-lg text-xs text-[#0b1a2e]/70">
        <Info className="w-4 h-4 text-[#0f5c3d] mt-0.5 shrink-0" />
        <span>
          Body supports markdown-lite: <code>## Heading</code>, <code>- bullet</code>, <code>**bold**</code>. Paragraphs separated by blank lines.
        </span>
      </div>

      <div className="space-y-4">
        {form.sections.map((s, idx) => (
          <div
            key={s.id}
            data-testid="about-section-card"
            className="bg-white border border-black/5 rounded-2xl p-5"
          >
            <div className="flex items-center justify-between gap-3 mb-4">
              <div className="text-xs text-[#0b1a2e]/50 font-medium">Section {idx + 1}</div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => moveSection(idx, -1)}
                  disabled={idx === 0}
                  title="Move up"
                  className="p-2 rounded-lg hover:bg-[#f7fbf8] disabled:opacity-30"
                >
                  <ArrowUp className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => moveSection(idx, 1)}
                  disabled={idx === form.sections.length - 1}
                  title="Move down"
                  className="p-2 rounded-lg hover:bg-[#f7fbf8] disabled:opacity-30"
                >
                  <ArrowDown className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => removeSection(idx)}
                  title="Delete"
                  className="p-2 rounded-lg text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <label className="md:col-span-2 block">
                <span className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">Heading</span>
                <input
                  value={s.heading}
                  onChange={(e) => patchSection(idx, 'heading', e.target.value)}
                  className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0f5c3d]/30"
                />
              </label>
              <label className="block">
                <span className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">Image position</span>
                <select
                  value={s.image_position}
                  onChange={(e) => patchSection(idx, 'image_position', e.target.value)}
                  className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0f5c3d]/30 bg-white"
                >
                  <option value="none">No image</option>
                  <option value="left">Image on left</option>
                  <option value="right">Image on right</option>
                </select>
              </label>
            </div>

            <label className="block mt-4">
              <span className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">Body (markdown-lite)</span>
              <textarea
                rows={6}
                value={s.body}
                onChange={(e) => patchSection(idx, 'body', e.target.value)}
                className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0f5c3d]/30 resize-y font-mono"
              />
            </label>

            {s.image_position !== 'none' && (
              <div className="mt-4">
                <span className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">Image</span>
                <div className="flex items-start gap-3">
                  <div className="w-32 aspect-[16/10] rounded-lg overflow-hidden bg-[#e2ede6] flex items-center justify-center shrink-0">
                    {s.image_url ? (
                      <img src={resolveFileUrl(s.image_url)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <ImageIcon className="w-5 h-5 text-[#0b1a2e]/30" />
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-[#0f5c3d]/20 text-xs cursor-pointer hover:bg-[#f7fbf8]">
                      {uploadingIdx === idx ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                      Upload
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => handleSectionUpload(idx, e.target.files?.[0])} />
                    </label>
                    <input
                      value={s.image_url || ''}
                      onChange={(e) => patchSection(idx, 'image_url', e.target.value)}
                      placeholder="or paste URL"
                      className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-1.5 text-xs outline-none"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AboutAdminPage;
